import type { Licence as CaMyProfileLicence } from '@seek/ca-myprofile-utils';

import type { RoleProps } from 'src/App/AppPageBlock/ProfileDrawer/Profile/Role/Role';
import type { CandidateProfileNationality } from 'src/App/AppPageBlock/graphql/hirer-types.generated';

import type {
  IEducation,
  ILicence,
  IWorkHistory,
  ISkill,
  IProfileStore,
} from '../../../../../../stores/JobApplicationStore/ProfileStore';

import type { EducationWithCredential, Profile } from './types';

const transformLicences = (licence: ILicence): CaMyProfileLicence => ({
  id: licence.id,
  name: licence.name,
  issuingOrganisation: licence.issuingOrganisation ?? undefined,
  issueDate: licence.issueDate ?? undefined,
  expiryDate: licence.expiryDate ?? undefined,
  credential: licence.credential
    ? {
        credentialInfo: licence.credential.credentialInfo,
        deleteVerificationUrl: 'deleteVerificationUrl',
        manageVerificationUrl: 'manageVerificationUrl',
        verification: {
          result: '',
        },
      }
    : undefined,
  noExpiryDate: licence.noExpiryDate,
  description: licence.description ?? undefined,
  formattedDate: licence.formattedExpiryDate ?? undefined,
});

const transformEducation = (education: IEducation): EducationWithCredential => {
  const completed = education.completed;
  const educationItem = {
    id: education.id,
    name: education.name,
    institute: education.institute,
    highlights: education.highlights ?? undefined,
  };
  if (completed) {
    return {
      ...educationItem,
      completed,
      completionDate: education.completionDate
        ? {
            year: education.completionDate.year,
          }
        : undefined,
      credential: education.credential
        ? {
            ...education.credential,
            deleteVerificationUrl: 'http://certsy',
            manageVerificationUrl: 'http://certsy',
            verification: {
              result: 'result',
            },
          }
        : undefined,
    };
  }

  return {
    ...educationItem,
    completed,
    completionDate:
      education.completionDate && education.completionDate.month
        ? {
            year: education.completionDate.year,
            month: education.completionDate.month,
          }
        : undefined,
  };
};

export const transformWorkHistory = (workHistory: IWorkHistory): RoleProps => ({
  title: workHistory.title.text,
  company: workHistory.company.text,
  from: workHistory.startDate,
  to: workHistory.endDate,
  achievements: workHistory.achievements,
});

const transformSkills = (skills: ISkill): string => skills.keyword.text;

const transformNationalities = (
  nationality: CandidateProfileNationality,
): string => nationality.countryName;

export const transformProfileToComponentProps = (
  profileData: IProfileStore,
): Profile | null => {
  const skills: string[] = profileData.skills.map((skill) =>
    transformSkills(skill),
  );
  const qualifications = profileData.education.map((education) =>
    transformEducation(education),
  );

  const roles = profileData.workHistory.map((workHistory) =>
    transformWorkHistory(workHistory),
  );

  const licences = profileData.licences.map((licence) =>
    transformLicences(licence),
  );

  const rightsToWork = profileData.rightsToWork.map((rightToWork) => ({
    answer: rightToWork.answer,
    country: rightToWork.country,
  }));

  const rightsToWorkV2 = profileData.rightsToWorkV2.map((rightToWorkV2) => ({
    displayLabel: rightToWorkV2.displayLabel,
    id: rightToWorkV2.id,
  }));

  const verifiedIdentity = profileData.verifiedIdentity;
  const homeLocation = profileData.homeLocation?.description ?? undefined;

  const nationalities = profileData.nationality.map(transformNationalities);

  const profile = {
    roles,
    qualifications,
    skills,
    licences,
    rightsToWork,
    verifiedIdentity,
    homeLocation,
    nationalities,
    rightsToWorkV2,
  };

  if (
    roles.length === 0 &&
    qualifications.length === 0 &&
    skills.length === 0 &&
    licences.length === 0 &&
    rightsToWork.length === 0 &&
    !homeLocation &&
    nationalities.length === 0 &&
    rightsToWorkV2.length === 0
  ) {
    return null;
  }

  return profile;
};

export default transformProfileToComponentProps;
